import styles from 'components/search/search-columns/source-column/source-column-filter.module.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import React, { useCallback, useMemo } from 'react'

import {
  selectSearchPage,
  setSourceScope,
} from '@disco/state-management/reducers/search-page'
import { hasActiveFilters, resolveSourceScope } from '@disco/utils/search-page'

import type { SourceScope } from 'types/search-page'

import { Checkbox, DropdownMenu, Flex, Text, Tooltip } from 'components/ds'
import { ColumnHeaderFilterIcon } from 'components/search/search-columns/column-header-filter-icon'

import { useDispatch, useSelector } from 'hooks/use-redux'

const MDS_SUPPORT_URL =
  'https://support.disco.ac/home/multidisco-search-feature-guide-for-music-supervisors'

const SOURCE_OPTIONS: {
  label: string
  value: SourceScope
  tooltip?: string
}[] = [
  { label: 'My DISCO', value: 'business' },
  {
    label: 'Search across DISCO',
    value: 'mds',
    tooltip: 'What is this?',
  },
]

export const SourceColumnFilter = () => {
  const dispatch = useDispatch()
  const { sourceScope, hasSubmittedSearch, filters } =
    useSelector(selectSearchPage)

  // Flag (not live query) so checkboxes don't flip per keystroke; filters stay
  // live since filter changes refetch atomically.
  const hasSearched = useMemo(
    () => hasSubmittedSearch || hasActiveFilters(filters),
    [hasSubmittedSearch, filters],
  )

  // `sourceScope` null = untouched popover; resolve so checkboxes mirror the
  // searched scope (business-only browsing, both once searched).
  const displayedScopes = useMemo<SourceScope[]>(
    () => resolveSourceScope(sourceScope, hasSearched),
    [sourceScope, hasSearched],
  )

  const handleToggle = useCallback(
    (value: SourceScope) => {
      const newScopes = displayedScopes.includes(value)
        ? displayedScopes.filter(s => s !== value)
        : [...displayedScopes, value]

      // Defensive fallback: the dropdown disables the last checked item so
      // this branch is unreachable from the UI, but guards against future
      // changes (keyboard shortcuts, programmatic toggles, etc.).
      const [firstScope, ...restScopes] = newScopes
      if (!firstScope) return

      // The search page's change-detection effect fetches on sourceScope change.
      dispatch(setSourceScope([firstScope, ...restScopes]))
    },
    [dispatch, displayedScopes],
  )

  // Highlight only when the user has explicitly narrowed the scope, not for the
  // business-only browse default (`sourceScope === null`).
  const isFiltered = Boolean(
    sourceScope && sourceScope.length < SOURCE_OPTIONS.length,
  )

  return (
    <Flex align="center" gap="2">
      Source
      <Flex onClick={e => e.stopPropagation()}>
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger>
            <ColumnHeaderFilterIcon
              isFiltered={isFiltered}
              aria-label="Filter source results"
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content variant="soft">
            {SOURCE_OPTIONS.map(option => {
              const isChecked = displayedScopes.includes(option.value)
              const isLastChecked = isChecked && displayedScopes.length === 1

              return (
                <DropdownMenu.Item
                  key={option.value}
                  disabled={isLastChecked}
                  onSelect={e => {
                    e.preventDefault()
                    handleToggle(option.value)
                  }}
                  asChild
                >
                  <Text size="2" as="div" className={styles.optionLabel}>
                    <Flex align="center" gap="2">
                      <Checkbox checked={isChecked} disabled={isLastChecked} />
                      {option.label}
                      {option.tooltip && (
                        <Tooltip content={option.tooltip} avoidCollisions>
                          <a
                            href={MDS_SUPPORT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Info about results from across DISCO"
                            onClick={e => e.stopPropagation()}
                            className={styles.infoLink}
                          >
                            <RxInfoCircled />
                          </a>
                        </Tooltip>
                      )}
                    </Flex>
                  </Text>
                </DropdownMenu.Item>
              )
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>
    </Flex>
  )
}
